import React, { useRef, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getMyAssignmentsAction } from '../../../../store/reducers/personalTask';

const NotificationsDropdown = () => {
    const dropdownRef = useRef(null);
    const [t] = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();

    const [dropdownState, updateDropdownState] = useState({
        isDropdownOpen: false
    });

    const myAssignments = useSelector(state => state.personalTask?.myAssignments || []);

    useEffect(() => {
        // Fetch assignments on mount to populate notifications if not already fetched
        dispatch(getMyAssignmentsAction({}));
    }, [dispatch]);

    const toggleDropdown = () => {
        updateDropdownState({ isDropdownOpen: !dropdownState.isDropdownOpen });
    };

    const handleClickOutside = (event) => {
        if (
            dropdownRef &&
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
        ) {
            updateDropdownState({ isDropdownOpen: false });
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside, false);
        return () => {
            document.removeEventListener(
                'mousedown',
                handleClickOutside,
                false
            );
        };
    }, []);

    const notifications = [];
    const now = new Date();

    // Sort assignments by some logical order if needed. We'll just iterate.
    myAssignments.forEach(task => {
        // Skip if the project is already completed
        if (Number(task.projectStatus ?? task.ProjectStatus ?? 0) === 1) {
            return;
        }

        const status = Number(task.status ?? task.Status ?? 0);

        // 1. New tasks (Cần làm)
        if (status === 0) {
            notifications.push({
                type: 'new',
                title: 'Nhiệm vụ mới',
                message: task.projectName || 'Dự án không tên',
                icon: 'fas fa-tasks text-info',
                date: task.startDate ? new Date(task.startDate).getTime() : now.getTime(),
                link: '/project/personal-kanban'
            });
        }

        // 2. Expiring tasks (not completed, not cancelled, and <= 2 days left)
        if (status !== 1 && status !== 3 && task.endDate) {
            const endDate = new Date(task.endDate);
            const diffTime = endDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays >= 0 && diffDays <= 2) {
                notifications.push({
                    type: 'warning',
                    title: diffDays === 0 ? 'Hết hạn hôm nay' : `Còn ${diffDays} ngày hết hạn`,
                    message: task.projectName || 'Dự án không tên',
                    icon: 'fas fa-exclamation-triangle text-warning',
                    date: endDate.getTime(),
                    link: '/project/personal-kanban'
                });
            } else if (diffDays < 0) {
                notifications.push({
                    type: 'danger',
                    title: 'Đã quá hạn',
                    message: task.projectName || 'Dự án không tên',
                    icon: 'fas fa-exclamation-circle text-danger',
                    date: endDate.getTime(),
                    link: '/project/personal-kanban'
                });
            }
        }
    });

    // Sort notifications by date descending
    notifications.sort((a, b) => b.date - a.date);

    let className = 'dropdown-menu dropdown-menu-lg dropdown-menu-right';

    if (dropdownState.isDropdownOpen) {
        className += ' show';
    }

    const handleNotificationClick = (e, link) => {
        e.preventDefault();
        updateDropdownState({ isDropdownOpen: false });
        if (link) {
            history.push(link);
        }
    };

    return (
        <li
            ref={dropdownRef}
            className="nav-item d-none d-sm-inline-block dropdown"
        >
            <style>
                {`
                @keyframes bell-ring {
                    0% { transform: rotate(0); }
                    10% { transform: rotate(15deg); }
                    20% { transform: rotate(-10deg); }
                    30% { transform: rotate(15deg); }
                    40% { transform: rotate(-10deg); }
                    50% { transform: rotate(0); }
                    100% { transform: rotate(0); }
                }
                .bell-animated {
                    animation: bell-ring 2s infinite;
                    display: inline-block;
                    transform-origin: top center;
                }
                `}
            </style>
            <button
                onClick={toggleDropdown}
                type="button"
                className="nav-link"
                data-toggle="dropdown"
                style={{ position: 'relative' }}
            >
                <i className={`far fa-bell ${notifications.length > 0 ? 'bell-animated' : ''}`} style={{ fontSize: '1.4rem', color: notifications.length > 0 ? '#ffc107' : 'inherit' }} />
                {notifications.length > 0 && (
                    <span className="badge badge-danger navbar-badge" style={{ fontSize: '0.65rem', right: '0px', top: '2px' }}>{notifications.length}</span>
                )}
            </button>
            <div className={className} style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <span className="dropdown-item dropdown-header">
                    {notifications.length} Thông báo
                </span>

                {notifications.length > 0 ? notifications.slice(0, 8).map((notif, index) => (
                    <React.Fragment key={index}>
                        <div className="dropdown-divider" />
                        <a href="#" onClick={(e) => handleNotificationClick(e, notif.link)} className="dropdown-item" style={{ whiteSpace: 'normal', padding: '10px 15px' }}>
                            <div className="d-flex align-items-start">
                                <i className={`${notif.icon} mt-1 mr-3`} style={{ fontSize: '1.2rem' }} />
                                <div>
                                    <h6 className="mb-1" style={{ fontSize: '14px', fontWeight: 'bold' }}>{notif.title}</h6>
                                    <div className="text-muted text-sm" style={{ lineHeight: '1.4' }}>{notif.message}</div>
                                </div>
                            </div>
                        </a>
                    </React.Fragment>
                )) : (
                    <>
                        <div className="dropdown-divider" />
                        <span className="dropdown-item text-muted text-center py-3">
                            Không có thông báo mới
                        </span>
                    </>
                )}

                <div className="dropdown-divider" />
                <a href="#" onClick={(e) => handleNotificationClick(e, '/project/personal-kanban')} className="dropdown-item dropdown-footer text-center">
                    Xem tất cả trong bảng tiến độ
                </a>
            </div>
        </li>
    );
};

export default NotificationsDropdown;
